import { createBigDecimal } from '@0xtorch/big-decimal'
import { z } from 'zod'

export const tradeSchema = z.object({
  /** e.g. `"BNBBTC"` */
  symbol: z.string(),
  /** e.g. `28457` */
  id: z
    .number()
    .int()
    .transform((v) => v.toString()),
  /** e.g. `100234` */
  orderId: z.number(),
  /** Unless OCO, the value will always be -1 */
  orderListId: z.number().int(),
  /** e.g. `"4.00000100"` */
  price: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. `"12.00000000"` */
  qty: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. `"48.000012"` */
  quoteQty: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. `"10.10000000"` */
  commission: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. `"BNB"` */
  commissionAsset: z.string(),
  /** e.g. `1499865549590` */
  time: z.number().int(),
  isBuyer: z.boolean(),
  isMaker: z.boolean(),
  isBestMatch: z.boolean(),
})

export type Trade = z.infer<typeof tradeSchema>

export const symbolSchema = z.object({
  /** e.g. `"ETHBTC"` */
  symbol: z.string(),
  /** e.g. `"TRADING"` */
  status: z.string(),
  /** e.g. `"ETH"` */
  baseAsset: z.string(),
  /** e.g. `8` */
  baseAssetPrecision: z.number().int(),
  /** e.g. `"BTC"` */
  quoteAsset: z.string(),
  /** e.g. `8` */
  quotePrecision: z.number().int(),
  /** e.g. `8` */
  quoteAssetPrecision: z.number().int(),
  /** e.g. `["LIMIT", "LIMIT_MAKER", "MARKET", "STOP_LOSS", "STOP_LOSS_LIMIT", "TAKE_PROFIT", "TAKE_PROFIT_LIMIT"]` */
  orderTypes: z.array(z.string()),
  icebergAllowed: z.boolean(),
  ocoAllowed: z.boolean(),
  quoteOrderQtyMarketAllowed: z.boolean(),
  allowTrailingStop: z.boolean(),
  cancelReplaceAllowed: z.boolean(),
  isSpotTradingAllowed: z.boolean(),
  isMarginTradingAllowed: z.boolean(),
  /** These are defined in the Filters section. All filters are optional */
  filters: z.array(z.unknown()),
  permissions: z.array(z.string()),
  /** e.g. `[["SPOT", "MARGIN"]]` */
  permissionSets: z.array(z.array(z.string())),
  /** e.g. `"NONE"` */
  defaultSelfTradePreventionMode: z.string(),
  /** e.g. `["NONE"]` */
  allowedSelfTradePreventionModes: z.array(z.string()),
})

export type BinanceSymbol = z.infer<typeof symbolSchema>
